import { useValidateInvoicePersonDataMutation } from "src/services/cartServices";
import React, { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Select, Skeleton } from "antd";
import toast from "react-hot-toast";
import { toastWarningStyle, useErrorFieldsNeedReviewToast } from "src/constants";
import { Flex } from "@nubeteck/components";
import { ArrayUtils } from "@nubeteck/utils";
import { useLocale } from "../../../i18n/localeContext";
import { getUserEnrollementPage } from "../../../Api/content";
import { StrapiError } from "../../../pages/StrapiError";

interface IInvoicePersonData {
  terceroNombre: string;
  terceroCorreo: string;
  terceroCedula: string;
  terceroActividadEconomica?: string;
  existeEnHacienda: boolean;
}

function ThirdPersonForm({
  savePerson,
  isLoading,
  open,
  onCancel,
  initialValues,
}: {
  savePerson: (values: IInvoicePersonData) => void;
  isLoading: boolean;
  open: boolean;
  onCancel: () => void;
  initialValues: IInvoicePersonData;
}) {
  const [formInvoiceData] = Form.useForm<IInvoicePersonData>();
  const [actividades, setActividades] = useState([]);
  const [dataValidated, setDataValidated] = useState(false);
  const { validateFields, setFieldsValue, getFieldValue } = formInvoiceData;
  const { locale } = useLocale();  
  const [userEnrollementPage, setUserEnrollementPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const showErrorFieldsNeedReviewToast = useErrorFieldsNeedReviewToast();
  const terceroActividadEconomica = getFieldValue("terceroActividadEconomica");
  const [
    validatePerson,
    {
      isLoading: isLoadingValidatePerson,
      data: personValidation,
      error: errorValidatePerson,
      isSuccess: isSuccessPersonValidation,
    },
  ] = useValidateInvoicePersonDataMutation();

  useEffect(() => {
    setFieldsValue(initialValues);
    if (initialValues?.existeEnHacienda) setDataValidated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  useEffect(() => {
    if (errorValidatePerson && open) {
      toast.error(errorValidatePerson?.data?.message, toastWarningStyle);
      setActividades([]);
      setFieldsValue({ terceroNombre: "", terceroCorreo: "", terceroActividadEconomica: "" });
      setDataValidated(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorValidatePerson]);

  useEffect(() => {
    // Effect intentionally empty - dependencies tracked for future use
  }, [actividades, terceroActividadEconomica]);

  useEffect(() => {
    if (isSuccessPersonValidation) {
      if (personValidation?.data?.legalName) {
        setActividades(personValidation?.data?.actividades);
        setDataValidated(true);
        setFieldsValue({
          terceroNombre: personValidation?.data?.legalName,
          terceroActividadEconomica: personValidation?.data?.terceroActividadEconomica,
          terceroCorreo:
            personValidation?.data?.legalName == initialValues.terceroNombre
              ? initialValues.terceroCorreo
              : "",
        });
      } else setDataValidated(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personValidation, isSuccessPersonValidation]);

  const handleOkInvoicePerson = () => {
    validateFields(["terceroNombre", "terceroCorreo", "terceroCedula", "terceroActividadEconomica"])
      .then((values) => {
        if (values) {
          savePerson({ ...values, existeEnHacienda: dataValidated });
        }
      })
      .catch(() => {
        showErrorFieldsNeedReviewToast();
      });
  };

  const actividadesOptions = ArrayUtils.selectLabelValue(
    actividades ?? [],
    "descripcion",
    "codigo",
  );

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setUserEnrollementPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    getUserEnrollementPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setUserEnrollementPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setUserEnrollementPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setUserEnrollementPage(null);
      }}
    />;
  }
  return (
    <Modal
      closable={false}
      open={open}
      onOk={handleOkInvoicePerson}
      confirmLoading={isLoading}
      cancelText={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.box_selection.cancel_button || "Cancelar")}
      okText={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.box_selection.save_button || "Guardar")}
      onCancel={() => {
        onCancel();
        setFieldsValue(initialValues);
      }}
      style={{ maxWidth: 450 }}
    >
      <Form layout="vertical" form={formInvoiceData}>
        <Flex style={{ alignItems: "center", gap: 15 }}>
          <Form.Item
            name="terceroCedula"
            required
            label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.box_selection.id_student || "Cédula")}
            initialValue={initialValues?.terceroCedula}
            rules={[
              {
                required: true,
                message: "la cédula del responsable del pago es requerida.",
              },
            ]}
          >
            <Input placeholder="Cédula" size="large" />
          </Form.Item>
          <Button
            onClick={() => validatePerson(getFieldValue("terceroCedula"))}
            loading={isLoadingValidatePerson}
          >
            {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.validate_button || "Validar")}
          </Button>
        </Flex>

        <Form.Item
          name="terceroNombre"
          label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.box_selection.name_student || "Nombre")}
          initialValue={initialValues?.terceroNombre}
          rules={[
            {
              required: true,
              message: "El nombre al que estarán registradas las facturas es requerido.",
            },
          ]}
          required
        >
          <Input placeholder="Nombre" size="large" readOnly={dataValidated} />
        </Form.Item>

        <Form.Item
          name="terceroCorreo"
          required
          label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.box_selection.email_student || "Correo Electrónico")}
          initialValue={initialValues?.terceroCorreo}
          rules={[
            {
              required: true,
              message: "Este campo es requerido.",
            },
          ]}
        >
          <Input placeholder="Correo Electrónico" size="large" />
        </Form.Item>

        {/* {actividades.length > 0 ||
        (initialValues?.terceroActividadEconomica ?? "").trim() !== "" ? ( */}

        {((terceroActividadEconomica ?? "").trim() !== "" || actividades.length > 0) && (
          <>
            <Form.Item
              name="terceroActividadEconomica"
              required
              label={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.economic_activity || "Actividad económica")}
              initialValue={initialValues?.terceroActividadEconomica}
              rules={[
                {
                  required: true,
                  message: "Este campo es requerido.",
                },
              ]}
            >
              <Select
                options={actividadesOptions}
                // filterOption={filterOption}
                placeholder={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.select_option || "Seleccione una actividad")}
                showSearch
                optionFilterProp="children"
                allowClear
              />
            </Form.Item>
          </>
        )}

        {/* } */}

        {/* <ButtonAlternativeStyled
          disabled={isLoadingSavingPerson || isLoadingValidatePerson}
          size="small"
          type="default"
          onClick={() => onCancel()}
        >
          Reestablecer a valores por defecto
        </ButtonAlternativeStyled> */}
      </Form>
    </Modal>
  );
}

export default ThirdPersonForm;
